import { approxSign, approxZero, clamp, EPSILON, type Vec2, vec } from '../math';

import { type Line, type Ray, type Segment } from './types';

/**
 * Operations on lines, rays and segments.
 *
 * The three share almost all of their mathematics and differ only in how the parameter
 * `t` along the direction is restricted:
 *
 * | Shape   | Valid `t`      | `t = 0` | `t = 1`          |
 * | ------- | -------------- | ------- | ---------------- |
 * | Line    | `(-∞, ∞)`      | origin  | one unit along   |
 * | Ray     | `[0, ∞)`       | origin  | one unit along   |
 * | Segment | `[0, 1]`       | `a`     | `b`              |
 *
 * Note the difference in scale: line and ray directions are unit length, so `t` is a
 * distance, whereas a segment's `t` is a fraction of its length. Keeping that explicit
 * here avoids a whole class of subtle errors in the construction tools.
 */

/* ------------------------------------------------------------------------ line */

/** Point at parameter `t` (a signed distance from the origin). */
export function pointOnLine(l: Line, t: number): Vec2 {
  return vec.addScaled(l.origin, l.direction, t);
}

/** Signed distance along the line of the projection of `p`. */
export function parameterOnLine(l: Line, p: Vec2): number {
  return vec.dot(vec.subtract(p, l.origin), l.direction);
}

export function closestPointOnLine(l: Line, p: Vec2): Vec2 {
  return pointOnLine(l, parameterOnLine(l, p));
}

/**
 * Signed perpendicular distance from `p` to the line.
 *
 * Positive when `p` lies to the left of the direction of travel (counter-clockwise
 * side), which matches the sign convention of the 2D cross product.
 */
export function signedDistanceToLine(l: Line, p: Vec2): number {
  return vec.cross(l.direction, vec.subtract(p, l.origin));
}

export function distanceToLine(l: Line, p: Vec2): number {
  return Math.abs(signedDistanceToLine(l, p));
}

/** `1` for the left side, `-1` for the right, `0` when on the line within `epsilon`. */
export function sideOfLine(l: Line, p: Vec2, epsilon: number = EPSILON): -1 | 0 | 1 {
  return approxSign(signedDistanceToLine(l, p), epsilon);
}

export function isPointOnLine(l: Line, p: Vec2, epsilon: number = EPSILON): boolean {
  return approxZero(signedDistanceToLine(l, p), epsilon);
}

/** Reflects `p` across the line. */
export function reflectAcrossLine(l: Line, p: Vec2): Vec2 {
  const foot = closestPointOnLine(l, p);
  return vec.addScaled(foot, vec.subtract(foot, p), 1);
}

/** The line through `p` parallel to `l`. */
export function parallelThrough(l: Line, p: Vec2): Line {
  return { origin: p, direction: l.direction };
}

/** The line through `p` perpendicular to `l`. */
export function perpendicularThrough(l: Line, p: Vec2): Line {
  return { origin: p, direction: vec.perpendicular(l.direction) };
}

export function areLinesParallel(a: Line, b: Line, epsilon: number = EPSILON): boolean {
  return approxZero(vec.cross(a.direction, b.direction), epsilon);
}

export function areLinesPerpendicular(a: Line, b: Line, epsilon: number = EPSILON): boolean {
  return approxZero(vec.dot(a.direction, b.direction), epsilon);
}

/** True when the two lines are the same line — parallel *and* coincident. */
export function areLinesCoincident(a: Line, b: Line, epsilon: number = EPSILON): boolean {
  return areLinesParallel(a, b, epsilon) && isPointOnLine(a, b.origin, epsilon);
}

/* ------------------------------------------------------------------------- ray */

export function pointOnRay(r: Ray, t: number): Vec2 {
  return vec.addScaled(r.origin, r.direction, Math.max(0, t));
}

export function closestPointOnRay(r: Ray, p: Vec2): Vec2 {
  const t = vec.dot(vec.subtract(p, r.origin), r.direction);
  return vec.addScaled(r.origin, r.direction, Math.max(0, t));
}

export function distanceToRay(r: Ray, p: Vec2): number {
  return vec.distance(p, closestPointOnRay(r, p));
}

export function isPointOnRay(r: Ray, p: Vec2, epsilon: number = EPSILON): boolean {
  return distanceToRay(r, p) <= epsilon;
}

/* --------------------------------------------------------------------- segment */

/** Point at fraction `t` of the way from `a` to `b`. */
export function pointOnSegment(s: Segment, t: number): Vec2 {
  return vec.lerp(s.a, s.b, t);
}

/**
 * Fraction along the segment of the projection of `p`, **unclamped**.
 *
 * Values outside `[0, 1]` mean the projection falls beyond an endpoint, which callers
 * frequently need to know. Returns `0` for a degenerate segment.
 */
export function parameterOnSegment(s: Segment, p: Vec2): number {
  const d = vec.subtract(s.b, s.a);
  const lenSq = vec.lengthSquared(d);
  if (lenSq <= EPSILON * EPSILON) return 0;
  return vec.dot(vec.subtract(p, s.a), d) / lenSq;
}

export function closestPointOnSegment(s: Segment, p: Vec2): Vec2 {
  return vec.lerp(s.a, s.b, clamp(parameterOnSegment(s, p), 0, 1));
}

export function distanceToSegment(s: Segment, p: Vec2): number {
  return vec.distance(p, closestPointOnSegment(s, p));
}

/** Squared distance — use in comparisons to skip a square root. */
export function distanceSquaredToSegment(s: Segment, p: Vec2): number {
  return vec.distanceSquared(p, closestPointOnSegment(s, p));
}

export function isPointOnSegment(s: Segment, p: Vec2, epsilon: number = EPSILON): boolean {
  return distanceToSegment(s, p) <= epsilon;
}

export function segmentMidpoint(s: Segment): Vec2 {
  return vec.midpoint(s.a, s.b);
}

/** The perpendicular bisector of the segment, as an infinite line. */
export function perpendicularBisector(s: Segment): Line | null {
  const d = vec.subtract(s.b, s.a);
  if (vec.isZero(d)) return null;
  return {
    origin: vec.midpoint(s.a, s.b),
    direction: vec.normalize(vec.perpendicular(d)),
  };
}

/**
 * Whether `p` and `q` fall on opposite sides of the line through the segment.
 *
 * Points lying on the line count as neither side, so this returns `false` — the
 * behaviour segment-crossing tests want, where touching is handled separately.
 */
export function straddles(s: Segment, p: Vec2, q: Vec2, epsilon: number = EPSILON): boolean {
  const d = vec.subtract(s.b, s.a);
  const sp = approxSign(vec.cross(d, vec.subtract(p, s.a)), epsilon);
  const sq = approxSign(vec.cross(d, vec.subtract(q, s.a)), epsilon);
  return sp !== 0 && sq !== 0 && sp !== sq;
}
